import { useEffect, useState } from "react";
import { isMailEmbeddedInHub } from "@/auth/session";
import { useMailPwaInstall } from "@/hooks/useMailPwaInstall";
import {
  enableMailWebPush,
  getMailWebPushStatus,
  isMailWebPushSupported,
} from "@/lib/mail/mailWebPush";
import {
  MAIL_PWA_INSTALL_DISMISS_KEY,
  MAIL_PWA_PERMS_DISMISS_KEY,
  MAIL_PWA_PERMS_DONE_KEY,
  readLocalFlag,
  readSessionFlag,
  writeLocalFlag,
  writeSessionFlag,
} from "@/lib/pwa/mailPwaOnboard";

type Step = "install" | "perms" | null;

export function MailPwaOnboard() {
  const pwa = useMailPwaInstall();
  const [step, setStep] = useState<Step>(null);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState("");
  const [showIos, setShowIos] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [installDismissed, setInstallDismissed] = useState(() =>
    readSessionFlag(MAIL_PWA_INSTALL_DISMISS_KEY)
  );

  useEffect(() => {
    if (isMailEmbeddedInHub()) {
      setStep(null);
      return;
    }

    const permsDone = readLocalFlag(MAIL_PWA_PERMS_DONE_KEY);
    const permsDismissed = readSessionFlag(MAIL_PWA_PERMS_DISMISS_KEY);

    if (pwa.isStandalone) {
      if (permsDone || permsDismissed) {
        setStep(null);
        return;
      }
      if (!isMailWebPushSupported() || Notification.permission === "denied") {
        writeLocalFlag(MAIL_PWA_PERMS_DONE_KEY);
        setStep(null);
        return;
      }
      void getMailWebPushStatus().then((status) => {
        if (status === "subscribed" || status === "unsupported" || status === "not_configured") {
          writeLocalFlag(MAIL_PWA_PERMS_DONE_KEY);
          setStep(null);
          return;
        }
        setStep("perms");
      });
      return;
    }

    if (pwa.showInstallUi && !readSessionFlag(MAIL_PWA_INSTALL_DISMISS_KEY)) {
      setStep("install");
      return;
    }
    setStep(null);
  }, [pwa.isStandalone, pwa.showInstallUi]);

  if (isMailEmbeddedInHub()) return null;

  async function handleInstall() {
    if (pwa.canNativeInstall) {
      setBusy(true);
      try {
        const ok = await pwa.install();
        if (ok) setStep("perms");
      } finally {
        setBusy(false);
      }
      return;
    }
    if (pwa.isIos) {
      setShowIos(true);
      setShowManual(false);
      return;
    }
    setShowManual(true);
    setShowIos(false);
  }

  function dismissInstall() {
    writeSessionFlag(MAIL_PWA_INSTALL_DISMISS_KEY);
    setInstallDismissed(true);
    setStep(null);
  }

  function dismissPerms() {
    writeSessionFlag(MAIL_PWA_PERMS_DISMISS_KEY);
    setStep(null);
  }

  async function enablePerms() {
    setBusy(true);
    setHint("");
    const r = await enableMailWebPush();
    setBusy(false);
    if (r.ok) {
      writeLocalFlag(MAIL_PWA_PERMS_DONE_KEY);
      setStep(null);
      return;
    }
    if (r.status === "denied") {
      setHint("Notifications bloquées. Ouvrez les réglages du site dans le navigateur pour les autoriser.");
      return;
    }
    setHint(r.message || "Impossible d’activer les notifications.");
  }

  return (
    <>
      {step === "install" ? (
        <div className="mail-pwa-onboard-backdrop" onClick={dismissInstall}>
          <div
            className="mail-pwa-onboard"
            role="dialog"
            aria-labelledby="mail-pwa-install-title"
            onClick={(e) => e.stopPropagation()}
          >
            <img src="./favicon.png?v=20261009" alt="" className="mail-pwa-onboard__icon" />
            <h3 id="mail-pwa-install-title">Installez Aurélia Mail</h3>
            <p>
              Ajoutez l’application sur cet appareil pour ouvrir vos mails en un geste et recevoir les
              alertes.
            </p>
            <ul>
              <li>Icône sur le bureau ou l’écran d’accueil</li>
              <li>Session conservée</li>
              <li>Notifications dès qu’un message arrive</li>
            </ul>
            <button type="button" className="mail-pwa-onboard__cta" disabled={busy} onClick={() => void handleInstall()}>
              {busy ? "Installation…" : "Télécharger l’application"}
            </button>
            {showIos ? (
              <ol className="mail-pwa-onboard__steps">
                <li>
                  Appuyez sur <strong>Partager</strong> en bas de Safari
                </li>
                <li>
                  Choisissez <strong>Sur l’écran d’accueil</strong>
                </li>
                <li>
                  Confirmez avec <strong>Ajouter</strong>
                </li>
              </ol>
            ) : null}
            {showManual ? (
              <p className="mail-pwa-onboard__hint">
                Menu du navigateur (⋮) → <strong>Installer l’application</strong> ou{" "}
                <strong>Ajouter à l’écran d’accueil</strong>.
              </p>
            ) : null}
            <button type="button" className="mail-pwa-onboard__later" onClick={dismissInstall}>
              Plus tard
            </button>
          </div>
        </div>
      ) : null}

      {step === "perms" ? (
        <div className="mail-pwa-onboard-backdrop" onClick={dismissPerms}>
          <div
            className="mail-pwa-onboard"
            role="dialog"
            aria-labelledby="mail-pwa-perms-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="mail-pwa-perms-title">Autoriser les notifications</h3>
            <p>Le navigateur va vous demander l’accès. Acceptez pour être prévenu des nouveaux mails.</p>
            <button type="button" className="mail-pwa-onboard__cta" disabled={busy} onClick={() => void enablePerms()}>
              {busy ? "…" : "Autoriser les notifications"}
            </button>
            {hint ? <p className="mail-pwa-onboard__hint">{hint}</p> : null}
            <button type="button" className="mail-pwa-onboard__later" onClick={dismissPerms}>
              Plus tard
            </button>
          </div>
        </div>
      ) : null}

      {pwa.showInstallUi && installDismissed && step !== "install" ? (
        <button type="button" className="mail-pwa-onboard-pill" onClick={() => setStep("install")}>
          Télécharger l’application
        </button>
      ) : null}
    </>
  );
}
